/* global YT, Vimeo */
import fadeOut from './utils';

/**
 * Video BG class
 */
export default class UaVideoBg {
  /**
   * Constructor for UaVideoBg
   * @param {HTMLElement|string} el - The element or selector for the video background container.
   * @param {Object} settings - Custom settings for the video background.
   */
  constructor(el, settings) {
    this.$el = typeof el === 'string' ? document.querySelector(el) : el;
    this.defaults = {
      videoType: 'default',
      videoId: null,
      isLoop: true,
      ratio: '3x2',
    };
    this.dataSettings = this.$el.hasAttribute('data-ua-video-bg-options')
      ? JSON.parse(this.$el.getAttribute('data-ua-video-bg-options'))
      : {};
    this.settings = Object.assign({}, this.defaults, this.dataSettings, settings);

    // Parse the ratio string into a numeric value and store it
    this.settings.ratioValue = this._parseRatio(this.settings.ratio);
  }

  /**
   * Initialize the video background based on the type.
   */
  init() {
    this._prepareObject();

    switch (this.settings.videoType) {
      case 'youtube':
        this._initializeYouTube();
        break;
      case 'vimeo':
        this._initializeVimeo();
        break;
      default:
        this._initializeHTML5Video();
        break;
    }
  }

  /**
   * Prepare the video container and add preview image.
   * @private
   */
  _prepareObject() {
    this.$el.style.position = 'relative';

    const videoContainer = this._createVideoContainer();
    this.$el.insertAdjacentHTML('beforeend', videoContainer);
    this._addPreviewImage();
  }

  /**
   * Create the video container HTML based on the video type.
   * @returns {string} - The HTML string for the video container.
   * @private
   */
  _createVideoContainer() {
    const { videoType, videoId, isLoop } = this.settings;

    // Function to determine MIME type from file extension
    const getMimeType = (videoId) => {
      const extension = videoId.split('.').pop().toLowerCase();
      switch (extension) {
        case 'mp4':
          return 'video/mp4';
        case 'webm':
          return 'video/webm';
        case 'ogv':
          return 'video/ogg';
        case 'mov':
          return 'video/quicktime';
        default:
          console.warn(`Unsupported video format: ${extension}`);
          return 'video/mp4';
      }
    };

    switch (videoType) {
      case 'youtube':
        return '<div class="ua-video-bg-video"><div></div></div>';
      case 'vimeo':
        return '<div class="ua-video-bg-video"></div>';
      default: {
        const mimeType = getMimeType(videoId);
        return `
        <div class="ua-video-bg-video">
          <video poster="" autoplay muted ${isLoop ? 'loop' : ''}>
            <source src="${videoId}" type="${mimeType}">
            Your browser doesn't support HTML5 video.
          </video>
        </div>
      `;
      }
    }
  }

  /**
   * Add a preview image for the video background.
   * @private
   */
  _addPreviewImage() {
    const { videoType, videoId } = this.settings;

    if (videoType === 'youtube') {
      this.$el.insertAdjacentHTML(
        'beforeend',
        `<div class="ua-video-bg-preview" style="background-image: url(https://img.youtube.com/vi/${videoId}/maxresdefault.jpg);"></div>`,
      );
    } else if (videoType === 'vimeo') {
      this._fetchVimeoThumbnail(videoId);
    }
  }

  /**
   * Fetch and add the Vimeo thumbnail as a preview image.
   * @param {string} videoId - The Vimeo video ID.
   * @private
   */
  _fetchVimeoThumbnail(videoId) {
    fetch(`https://www.vimeo.com/api/v2/video/${videoId}.json`)
      .then((response) => response.json())
      .then((data) => {
        this.$el.insertAdjacentHTML(
          'beforeend',
          `<div class="ua-video-bg-preview" style="background-image: url(${data[0].thumbnail_large});"></div>`,
        );
      });
  }

  /**
   * Initialize the YouTube video background.
   * @private
   */
  _initializeYouTube() {
    this._APICreating('https://www.youtube.com/player_api', 'YT', 'YTDetect').then(() => {
      this._createYouTubePlayer();
    });
  }

  /**
   * Create the YouTube player and set its options.
   * @private
   */
  _createYouTubePlayer() {
    const { videoId, isLoop } = this.settings;
    const playerOptions = {
      videoId,
      playerVars: {
        autoplay: 1,
        controls: 0,
        showinfo: 0,
        enablejsapi: 1,
        modestbranding: 1,
        iv_load_policy: 3,
        loop: isLoop,
        playlist: videoId,
        origin: window.location.origin,
      },
      events: {
        onReady: (e) => {
          e.target.mute();
          e.target.playVideo();
          this._ratioCalc();
          window.addEventListener('resize', this._ratioCalc.bind(this));
          fadeOut(this.$el.querySelector('.ua-video-bg-preview'), 400);
        },
      },
    };

    new YT.Player(this.$el.querySelector('.ua-video-bg-video > div'), playerOptions);
  }

  /**
   * Initialize the Vimeo video background.
   * @private
   */
  _initializeVimeo() {
    this._APICreating('https://player.vimeo.com/api/player.js', 'Vimeo', 'VimeoDetect').then(() => {
      this._createVimeoPlayer();
    });
  }

  /**
   * Create the Vimeo player and set its options.
   * @private
   */
  _createVimeoPlayer() {
    const { videoId, isLoop } = this.settings;
    const playerOptions = {
      id: videoId,
      loop: isLoop,
      title: false,
      portrait: false,
      byline: false,
      autoplay: true,
      autopause: false,
      muted: true,
    };

    const vimeoPlayer = new Vimeo.Player(this.$el.querySelector('.ua-video-bg-video'), playerOptions);

    vimeoPlayer.play().then(() => {
      this._ratioCalc();
      window.addEventListener('resize', this._ratioCalc.bind(this));
      fadeOut(this.$el.querySelector('.ua-video-bg-preview'), 400);
    });
  }

  /**
   * Initialize the HTML5 video background.
   * @private
   */
  _initializeHTML5Video() {
    const video = this.$el.querySelector('.ua-video-bg-video video');
    if (video) {
      video.play().catch((error) => {
        console.warn('HTML5 video autoplay failed:', error);
      });

      // Add event listener for error handling
      video.addEventListener('error', (event) => {
        console.error('HTML5 video error:', event);
      });
    } else {
      console.error('HTML5 video element not found.');
    }

    window.addEventListener('resize', this._ratioCalc.bind(this));
    setTimeout(this._ratioCalc.bind(this));
  }

  /**
   * Parse a ratio string (e.g., '16x9') into a numeric value.
   * @param {string} ratio - The ratio string (e.g., '16x9').
   * @returns {number} The calculated numeric ratio.
   * @private
   */
  _parseRatio(ratio) {
    if (typeof ratio !== 'string') return 1; // Default fallback ratio
    const [width, height] = ratio.split('x').map(Number);
    return width && height ? width / height : 1; // Return numeric ratio or fallback
  }

  /**
   * Calculate and adjust the video ratio based on the container size.
   * @private
   */
  _ratioCalc() {
    const containerWidth = this.$el.clientWidth;
    const containerHeight = this.$el.clientHeight;

    const containerRatio = containerWidth / containerHeight;
    const $videoBg = this.$el.querySelector('.ua-video-bg-video');

    if (!$videoBg) return;

    // Adjust video size dynamically using the parsed ratio
    this._adjustVideoSizeForType(containerRatio, $videoBg);
  }

  /**
   * Adjust the video size for YouTube and Vimeo types.
   * @param {number} containerRatio - The ratio of the container width to height.
   * @param {HTMLElement} $videoBg - The video background container element.
   * @private
   */
  _adjustVideoSizeForType(containerRatio, $videoBg) {
    const videoRatio = this.settings.ratioValue; // Use parsed numeric ratio
    if (this.settings.videoType === 'youtube' || this.settings.videoType === 'vimeo') {
      if ($videoBg.clientHeight < $videoBg.clientHeight && window.innerWidth > 768) {
        $videoBg.style.width = `${containerRatio * this.$el.clientWidth}px`;
        $videoBg.style.height = `${this.$el.clientHeight}px`;
      } else {
        $videoBg.style.width = `${this.$el.clientWidth}px`;
        $videoBg.style.height = `${this.$el.clientWidth / videoRatio}px`;
      }
    }
  }

  /**
   * Load the required API script for YouTube or Vimeo.
   * @param {string} scriptUrl - The URL of the API script.
   * @param {string} globalName - The global object name to check for.
   * @param {string} globalNameDetect - The global object name to detect.
   * @returns {Promise} - A promise that resolves when the script is loaded.
   * @private
   */
  _APICreating(scriptUrl, globalName, globalNameDetect) {
    if (window[globalNameDetect]) {
      return Promise.resolve();
    }

    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      const before = document.querySelector('script');

      script.src = scriptUrl;
      before.parentNode.insertBefore(script, before);

      script.onload = () => {
        if (window[globalName]) {
          resolve();
        } else {
          reject(Error(`window.${globalName} undefined`));
        }
      };

      script.onerror = () => {
        reject(Error(`Error loading ${globalName || scriptUrl}`));
      };
    });
  }
}
